import { useId } from "react";
import { FormattedMessage } from "react-intl";
import {
  Avatar,
  Box,
  Collapse,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import {
  IconArrowDown,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";

import {
  formatMonthlyDifference,
  formatPercent,
  formatSekOrUnknown,
} from "../../utils/formatPension";
import { getPensionLogo } from "../../utils/pensionLogo";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";
import { SECONDARY_TEXT_COLOR } from "../../constants/colors";
import type { FundListProps, FundSwitchCardProps } from "../../types";
import { FundSwitchParts } from "./FundSwitchParts";
import classes from "./index.module.css";

function FundList({ funds, emphasised }: FundListProps) {
  const textColor = emphasised ? undefined : SECONDARY_TEXT_COLOR;

  return (
    <Stack component="ul" gap="xs" className={classes.list}>
      {funds.map((fund, index) => (
        <Group
          component="li"
          key={`${fund.isin ?? fund.name}-${index}`}
          justify="space-between"
          align="flex-start"
          wrap="nowrap"
          gap="md"
        >
          <Box>
            <Text size="sm" fw={emphasised ? 700 : 400} c={textColor}>
              {fund.name}
            </Text>
            {fund.isin && (
              <Text size="xs" c={SECONDARY_TEXT_COLOR}>
                {fund.isin}
              </Text>
            )}
          </Box>
          {typeof fund.shareOfTotal === "number" && (
            <Text
              size="sm"
              fw={emphasised ? 700 : 400}
              c={textColor}
              style={{ whiteSpace: "nowrap" }}
            >
              {formatPercent(fund.shareOfTotal)}
            </Text>
          )}
        </Group>
      ))}
    </Stack>
  );
}

export function FundSwitchCard({
  policy,
  adviceAge,
  opened,
  onToggle,
}: FundSwitchCardProps) {
  const detailsId = useId();

  const Logo = getPensionLogo(policy.logotype);

  const difference = policy.deltaFirstMonthPension;
  const hasDifference =
    typeof difference === "number" && Number.isFinite(difference);

  const { totalValue, fundPartValue, traditionalValue } = policy;
  // A combined policy: part of the capital is traditionally managed and stays as it is.
  const hasTraditionalPart =
    typeof fundPartValue === "number" &&
    typeof traditionalValue === "number" &&
    traditionalValue > 0;

  return (
    <Paper component="li" radius="lg" className={classes.card}>
      <Group gap="md" wrap="nowrap" align="center">
        <Avatar size="md" radius={0} classNames={{ root: classes.avatar }}>
          {Logo && <Logo />}
        </Avatar>
        <Box>
          <Title order={3} size="h5" mb={8}>
            {policy.insuranceCompany}
          </Title>
          {policy.owner?.legalName && (
            <Text
              size="sm"
              c={SECONDARY_TEXT_COLOR}
              className={REPLAY_BLOCK_CLASS}
            >
              {policy.owner.legalName}
            </Text>
          )}
          <Text
            size="sm"
            c={SECONDARY_TEXT_COLOR}
            className={REPLAY_BLOCK_CLASS}
          >
            {policy.insuranceIdentifier}
          </Text>
          <Text size="sm" fw={700} mt={8} className={REPLAY_BLOCK_CLASS}>
            {formatSekOrUnknown(totalValue)}
          </Text>
        </Box>
      </Group>

      {hasTraditionalPart && (
        <FundSwitchParts
          fundPartValue={fundPartValue}
          traditionalValue={traditionalValue}
        />
      )}

      <Collapse in={opened} id={detailsId}>
        <Stack gap="md" mt="md" className={classes.indented}>
          {policy.currentFunds.length > 0 && (
            <>
              <Stack gap="xs">
                <Text size="sm" c={SECONDARY_TEXT_COLOR} fw={700}>
                  <FormattedMessage
                    id="insurance.fundSwitch.current.title"
                    defaultMessage="Idag"
                  />
                </Text>
                <FundList funds={policy.currentFunds} emphasised={false} />
              </Stack>

              <Divider
                color="var(--wallet-color-neutrals-gray-200)"
                labelPosition="center"
                label={<IconArrowDown size={16} aria-hidden="true" />}
              />
            </>
          )}

          <Stack gap="xs">
            <Text size="sm" fw={700}>
              <FormattedMessage
                id="insurance.fundSwitch.recommendation.title"
                defaultMessage="Enligt råd"
              />
            </Text>
            <FundList funds={policy.recommendedFunds} emphasised />
          </Stack>

          {hasDifference && (
            <Box className={classes.effect}>
              {adviceAge !== undefined && (
                <Text size="sm" ta="center">
                  <FormattedMessage
                    id="insurance.fundSwitch.effect"
                    defaultMessage="Bytets förväntade effekt vid {age} år"
                    values={{ age: adviceAge }}
                  />
                </Text>
              )}
              <Text
                size="m"
                fw={700}
                ta="center"
                c={
                  difference < 0
                    ? "var(--wallet-color-sambla-red-shade-700)"
                    : "var(--wallet-color-sambla-green-shade-700)"
                }
                className={REPLAY_BLOCK_CLASS}
              >
                {formatMonthlyDifference(difference)}
              </Text>
            </Box>
          )}
        </Stack>
      </Collapse>

      <UnstyledButton
        onClick={onToggle}
        aria-expanded={opened}
        aria-controls={detailsId}
        className={classes.toggle}
      >
        <Text span size="xs" fw={700}>
          {opened ? (
            <FormattedMessage
              id="insurance.fundSwitch.collapseCard"
              defaultMessage="Dölj ändringar"
            />
          ) : (
            <FormattedMessage
              id="insurance.fundSwitch.expandCard"
              defaultMessage="Se ändringar"
            />
          )}
        </Text>
        {opened ? (
          <IconChevronUp size={14} aria-hidden="true" />
        ) : (
          <IconChevronDown size={14} aria-hidden="true" />
        )}
      </UnstyledButton>
    </Paper>
  );
}
